import React, { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../../../supabase';
import Modal from '../../../../components/Modal';
import NcrFormFields from '../NcrFormFields';
import { badge } from '../../badges';
import {
  Ncr,
  NcrFormData,
  NcrFormErrors,
  emptyNcrForm,
  validateNcr,
  withProjectSelection,
  withClientSelection,
  withContactSelection,
  ncrColumnValues,
} from '../../config';

interface TeamMember {
  id: number | string;
  first_name: string;
  last_name: string;
}

interface NcrDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  ncr: Ncr | null;
  onNcrUpdated: () => void;
  onNcrDeleted: () => void;
  teamMembers: TeamMember[];
}

const ncrToForm = (ncr: Ncr): NcrFormData => ({
  ...emptyNcrForm(),
  type: ncr.type,
  title: ncr.title || '',
  project_id: ncr.project_id != null ? ncr.project_id.toString() : '',
  stage_id: ncr.stage_id != null ? ncr.stage_id.toString() : null,
  raised_by: ncr.raised_by?.id != null ? ncr.raised_by.id.toString() : '',
  client_id: ncr.client_id != null ? ncr.client_id.toString() : '',
  contact_id: ncr.contact_id != null ? ncr.contact_id.toString() : '',
  date_raised: ncr.date_raised || '',
  classification: ncr.classification || '',
  severity: ncr.severity || '',
  description: ncr.description || '',
  requirement_not_met: ncr.requirement_not_met || '',
  impact: ncr.impact || '',
  external_reported_details: ncr.external_reported_details || '',
  internal_investigation: ncr.internal_investigation || '',
  action_to_be_taken: ncr.action_to_be_taken || '',
  status: ncr.status,
  target_close_out_date: ncr.target_close_out_date || '',
  actual_close_out_date: ncr.actual_close_out_date || '',
});

const NcrDetailModal: React.FC<NcrDetailModalProps> = ({
  isOpen,
  onClose,
  ncr,
  onNcrUpdated,
  onNcrDeleted,
  teamMembers,
}) => {
  const [formData, setFormData] = useState<NcrFormData | null>(null);
  const [errors, setErrors] = useState<NcrFormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ncr && isOpen) {
      setFormData(ncrToForm(ncr));
      setErrors({});
      setError(null);
      setConfirmDelete(false);
    }
  }, [ncr, isOpen]);

  const clearError = useCallback((field: keyof NcrFormErrors) => {
    setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
  }, []);

  const handleChange = useCallback(<K extends keyof NcrFormData>(field: K, value: NcrFormData[K]) => {
    setFormData(prev => (prev ? { ...prev, [field]: value } : prev));
    clearError(field as keyof NcrFormErrors);
  }, [clearError]);

  const handleProjectChange = useCallback((projectId: string | number | null, project?: any) => {
    setFormData(prev => (prev ? withProjectSelection(prev, projectId, project) : prev));
  }, []);

  const handleClientChange = useCallback((clientId: string | number | null) => {
    setErrors(prev => ({ ...prev, client_id: undefined, contact_id: undefined }));
    setFormData(prev => (prev ? withClientSelection(prev, clientId) : prev));
  }, []);

  const handleContactChange = useCallback((contactId: string | number | null) => {
    clearError('contact_id');
    setFormData(prev => (prev ? withContactSelection(prev, contactId) : prev));
  }, [clearError]);

  const handleSave = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ncr || !formData) return;

    const newErrors = validateNcr(formData);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setError('Please complete the required fields before saving.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('non_conformances')
        .update(ncrColumnValues(formData))
        .eq('id', ncr.id);

      if (updateError) throw updateError;
      onNcrUpdated();
    } catch (err) {
      console.error('Error updating non-conformance:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while saving');
    } finally {
      setIsSaving(false);
    }
  }, [ncr, formData, onNcrUpdated]);

  const handleDelete = useCallback(async () => {
    if (!ncr) return;
    setIsDeleting(true);
    setError(null);
    try {
      const { error: deleteError } = await supabase
        .from('non_conformances')
        .delete()
        .eq('id', ncr.id);
      if (deleteError) throw deleteError;
      onNcrDeleted();
    } catch (err) {
      console.error('Error deleting non-conformance:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while deleting');
      setIsDeleting(false);
    }
  }, [ncr, onNcrDeleted]);

  if (!ncr || !formData) return null;

  const headerStatus = badge(formData.status);

  const modalFooter = (
    <div className="modal-footer-actions">
      {confirmDelete ? (
        <div className="delete-confirm">
          <span>Delete this NCR?</span>
          <button type="button" onClick={() => setConfirmDelete(false)} className="btn-secondary">
            Cancel
          </button>
          <button type="button" onClick={handleDelete} disabled={isDeleting} className="btn-danger">
            {isDeleting ? 'Deleting...' : 'Confirm Delete'}
          </button>
        </div>
      ) : (
        <>
          <button type="button" onClick={() => setConfirmDelete(true)} className="btn-danger">
            Delete
          </button>
          <button type="submit" form="ncr-detail-form" disabled={isSaving} className="btn-primary">
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={ncr.reference}
      size="md"
      className="ncr-modal"
      hasFooter={true}
      footerContent={modalFooter}
      headerStatus={headerStatus}
    >
      <div className="form-content">
        <form id="ncr-detail-form" onSubmit={handleSave}>
          {error && <div className="error-message general-error">{error}</div>}
          <NcrFormFields
            formData={formData}
            onChange={handleChange}
            onProjectChange={handleProjectChange}
            onClientChange={handleClientChange}
            onContactChange={handleContactChange}
            teamMembers={teamMembers}
            errors={errors}
            required
          />
        </form>
      </div>
    </Modal>
  );
};

export default NcrDetailModal;
